import { withTiming } from '@common/core/utils/withTiming'
import { getLastUsedWorkspaceIdFromUserStore } from '@common/modules/user/utils/getLastUsedWorkspaceIdFromUserStore'

import type { Workspace } from '../types'
import { resolveWorkspaceFromList } from '../utils/resolveWorkspaceFromList'
import { createWorkspace as _createWorkspace, updateLastUsedProject as _updateLastUsedProject } from '../workspaces'

import { getWorkspacesByUserIdDeduped, resetWorkspacesFetchDedupedInFlight } from './utils/getWorkspacesByUserIdDeduped'
import { useWorkspacesStore } from './workspaces.store'

const runGetWorkspacesByUserIdWithTiming = withTiming('getWorkspacesByUserId', getWorkspacesByUserIdDeduped)

export type GetWorkspacesByUserIdOptions = {
  force?: boolean
}

export function resetWorkspacesFetchInFlight(): void {
  resetWorkspacesFetchDedupedInFlight()
  useWorkspacesStore.getState().setState({ userWorkspacesFetched: false })
}

async function loadUserWorkspacesIntoStoreIfNeeded(force = false): Promise<Workspace[]> {
  const { setState, userWorkspacesFetched, workspaces } = useWorkspacesStore.getState()
  if (userWorkspacesFetched && !force) {
    return workspaces
  }

  if (force) {
    resetWorkspacesFetchDedupedInFlight()
  }

  const data = await runGetWorkspacesByUserIdWithTiming()
  setState({ workspaces: data, userWorkspacesFetched: true })
  return data
}

export async function getWorkspacesByUserId(options?: GetWorkspacesByUserIdOptions): Promise<void> {
  const { setState } = useWorkspacesStore.getState()
  setState({ loading: true, error: null })
  try {
    await loadUserWorkspacesIntoStoreIfNeeded(options?.force ?? false)
  } catch (err) {
    setState({ workspaces: [], userWorkspacesFetched: false })
    throw err
  } finally {
    setState({ loading: false })
  }
}

/**
 * Sets currentWorkspace from the loaded list, preferring the user's last-used workspace.
 */
export function getCurrentWorkspace(): Workspace | null {
  const { workspaces } = useWorkspacesStore.getState()
  const workspace = resolveWorkspaceFromList(workspaces, getLastUsedWorkspaceIdFromUserStore())
  useWorkspacesStore.getState().setState({ currentWorkspace: workspace })
  return workspace
}

export const createWorkspace = withTiming('createWorkspace', async (workspaceName: string): Promise<string | null> => {
  const { setState } = useWorkspacesStore.getState()
  try {
    setState({ loading: true, error: null })
    return await _createWorkspace(workspaceName)
  } finally {
    setState({ loading: false })
  }
})

export const updateLastUsedProject = withTiming(
  'updateLastUsedProject',
  async (workspaceId: string, lastUsedProjectId: string): Promise<void> => {
    const { currentWorkspace, workspaces, setState } = useWorkspacesStore.getState()
    const previousWorkspaces = workspaces
    const previousCurrentWorkspace = currentWorkspace

    setState({
      workspaces: workspaces.map((workspace) =>
        workspace.id === workspaceId ? { ...workspace, lastUsedProjectId } : workspace
      ),
      currentWorkspace:
        currentWorkspace?.id === workspaceId ? { ...currentWorkspace, lastUsedProjectId } : currentWorkspace,
    })

    try {
      await _updateLastUsedProject(workspaceId, lastUsedProjectId)
    } catch (error) {
      setState({
        workspaces: previousWorkspaces,
        currentWorkspace: previousCurrentWorkspace,
      })
      console.error('Failed to update last used project:', error)
    }
  }
)
